import { api } from "@/redux/api";

import { ExportFileInfo } from "../export/export.schema";

import {
	OrganizationInfo,
	OrganizationRequestParams,
	OrganizationListActionRequest,
	OrganizationListActionResponse,
	OrganizationExportRequest,
	OrganizationAddToTrackerRequest,
	OrganizationAddToTrackerResponse,
	UpdateOrganizationRequest,
	UpdateOrganizationScoringRequest,
	PaginatedResponse,
	CompetitorAddRequest,
	CompetitorAddResult,
	CompetitorUnflagRequest,
	CompetitorReflagRequest,
	CompetitorUnflagResponse,
} from "../../pages/Contacts/schema/contacts";
import {
	OrganizationNormalizedName,
	OrganizationNormalizedNameSchema,
} from "../../pages/Contacts/schema/organization-normalized-name";

export const organizationApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getOrganizations: builder.query<PaginatedResponse<OrganizationInfo>, OrganizationRequestParams>(
			{
				query: ({
					sortBy,
					sortOrder,
					filter,
					searchValue,
					semanticSearchOrganizations,
					...params
				}) => ({
					url: "organization",
					params: {
						...(params && { ...params }),
						...(sortBy && { sortBy }),
						...(sortOrder && { sortOrder }),
						...(searchValue && { searchValue }),
						...(filter && { filter }),
						...(semanticSearchOrganizations && { semanticSearchOrganizations }),
					},
				}),
				providesTags: ["Organization"],
			}
		),
		updateOrganization: builder.mutation<OrganizationInfo, UpdateOrganizationRequest>({
			query: ({ id, ...body }) => ({
				url: `organization/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Organization"],
		}),
		updateOrganizationScoring: builder.mutation<OrganizationInfo, UpdateOrganizationScoringRequest>(
			{
				query: ({ organizationId, ...body }) => ({
					url: `organization/${organizationId}/scoring`,
					method: "PATCH",
					body,
				}),
				// Also refetch the Profile Tracker list so the Tracked Profiles tab's
				// Company Segment column updates without a manual page refresh (the tracker
				// rows are tagged "TrackerOrganization", not "Organization").
				invalidatesTags: ["Organization", "TrackerOrganization"],
			}
		),
		organizationAddToList: builder.mutation<
			OrganizationListActionResponse,
			OrganizationListActionRequest
		>({
			query: (body) => ({
				url: "organization/add-to-list",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization"],
		}),
		// Returns 202 with the export row to poll — see the Contacts equivalent (PREC-3953).
		requestOrganizationsExport: builder.mutation<ExportFileInfo, OrganizationExportRequest>({
			query: (body) => ({
				url: "organization/export",
				method: "POST",
				body,
			}),
		}),
		getCompetitors: builder.query<PaginatedResponse<OrganizationInfo>, OrganizationRequestParams>({
			query: ({ sortBy, sortOrder, filter, searchValue, ...params }) => ({
				url: "organization/competitors",
				params: {
					...(params && { ...params }),
					...(sortBy && { sortBy }),
					...(sortOrder && { sortOrder }),
					...(searchValue && { searchValue }),
					...(filter && { filter }),
				},
			}),
			providesTags: ["Organization"],
		}),
		competitorAddToList: builder.mutation<
			OrganizationListActionResponse,
			OrganizationListActionRequest
		>({
			query: (body) => ({
				url: "organization/add-to-list",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization"],
		}),
		downloadCompetitors: builder.query<Blob, OrganizationExportRequest>({
			query: (body) => ({
				url: "organization/export",
				method: "POST",
				body,

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		/**
		 * "Not a competitor": clears the Competitor category, removes the COMPETITOR
		 * blacklist entries, restores the companies' leads in their lists and records
		 * the decision so scoring cannot flag them again.
		 *
		 * Invalidates Blacklist and List too — the same action edits both.
		 */
		unflagCompetitors: builder.mutation<CompetitorUnflagResponse, CompetitorUnflagRequest>({
			query: (body) => ({
				url: "organization/competitors/unflag",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization", "Blacklist", "List"],
		}),
		/** The undo behind the success toast. */
		reflagCompetitors: builder.mutation<CompetitorUnflagResponse, CompetitorReflagRequest>({
			query: (body) => ({
				url: "organization/competitors/reflag",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization", "Blacklist", "List"],
		}),
		/**
		 * "Add competitors": marks companies as competitors by domain or LinkedIn company URL.
		 * An entry with no company behind it yet is blocked on its identifier alone, which is why
		 * this invalidates Blacklist as well (PREC-3594).
		 */
		addCompetitors: builder.mutation<CompetitorAddResult, CompetitorAddRequest>({
			query: (body) => ({
				url: "organization/competitors/add",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization", "Blacklist", "List"],
		}),
		uploadCompetitors: builder.mutation<CompetitorAddResult, FormData>({
			query: (body) => ({
				url: "organization/competitors/upload",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization", "Blacklist", "List"],
		}),
		/**
		 * Resolves `{prospect_organization_nameNormalized}` for one organization, filling it if it has
		 * never been resolved. A `query` rather than a `mutation` on purpose: the four campaign node
		 * cards previewing the same lead ask for the same organization, and only a query dedupes and
		 * caches that into a single request (PREC-3923).
		 */
		resolveOrganizationNormalizedName: builder.query<OrganizationNormalizedName, number>({
			query: (organizationId) => ({
				url: `organization/${organizationId}/normalized-name`,
				method: "POST",
				responseSchema: OrganizationNormalizedNameSchema,
			}),
		}),
		organizationAddToTracker: builder.mutation<
			OrganizationAddToTrackerResponse,
			OrganizationAddToTrackerRequest
		>({
			query: (body) => ({
				url: "organization/add-to-tracker",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Organization"],
		}),
	}),
});

export const {
	useGetOrganizationsQuery,
	useLazyGetOrganizationsQuery,
	useUpdateOrganizationMutation,
	useUpdateOrganizationScoringMutation,
	useOrganizationAddToListMutation,
	useOrganizationAddToTrackerMutation,
	useRequestOrganizationsExportMutation,
	useGetCompetitorsQuery,
	useCompetitorAddToListMutation,
	useLazyDownloadCompetitorsQuery,
	useUnflagCompetitorsMutation,
	useReflagCompetitorsMutation,
	useAddCompetitorsMutation,
	useUploadCompetitorsMutation,
	useResolveOrganizationNormalizedNameQuery,
} = organizationApi;
